import { useCallback, useEffect, useState } from 'react';
import { ProcessingRuleItem } from '@common-fe/common-fe';

import { MainInfo, PROCESSING_RULE_TYPE_KEY } from '../ProcessingRuleModal.types';

import useCreateProcessingRule from './useCreateProcessingRule';
import useProcessingRuleMainFields from './useProcessingRuleMainFields';
import useTiers from './useTiers';

const CONFIRM_TEXT = 'Changes that you made may not be saved.';

interface Params {
  updatedElement?: ProcessingRuleItem;
  visible?: boolean;
  onToggleVision?: (value: boolean) => void;
  onUpdateElement?: (ruleId?: string) => void;
}

const useProcessingRuleModal = ({ updatedElement, visible, onToggleVision, onUpdateElement }: Params) => {
  const [shouldConfirmOnClose, setShouldConfirmOnClose] = useState(false);
  const [state, setState] = useState<MainInfo>(() => ({
    name: updatedElement?.name || '',
    [PROCESSING_RULE_TYPE_KEY]: updatedElement?.processingRuleType,
  }));
  const [errorName, setErrorName] = useState<string>('');

  const fields = useProcessingRuleMainFields(state, updatedElement, { name: errorName || '' });

  const {
    fieldsValues: tiersValues,
    getFieldValuesById,
    handleChangeValues,
    handleRemoveById,
    handleAddNewTier,
  } = useTiers({ visible, updatedElement });

  // Modal close logic
  const handleClear = useCallback(() => {
    if (onUpdateElement) onUpdateElement(undefined);
    setErrorName('');
    setState({ name: '' });
  }, [onUpdateElement]);

  const handleCloseModal = useCallback(
    (value: boolean) => {
      if (onToggleVision) onToggleVision(value);
      handleClear();
    },
    [onToggleVision, handleClear]
  );

  const onCloseModalHandler = useCallback(
    (isConfirmMode?: boolean) => {
      if (isConfirmMode && shouldConfirmOnClose) {
        return window.confirm(CONFIRM_TEXT) && handleCloseModal(false);
      }
      return handleCloseModal(false);
    },
    [shouldConfirmOnClose, handleCloseModal]
  );

  // Submit logic
  const { isSubmitted, handleSubmit, isProcessingRuleNameError, setServerError, isServerError, isLoading } =
    useCreateProcessingRule({
      mainValue: state,
      visible,
      tiers: tiersValues,
      onClose: handleCloseModal,
      updatedElement,
      onCreated: (ruleId: string) => {
        if (onUpdateElement) {
          onUpdateElement(ruleId);
        }
      },
    });

  useEffect(() => {
    if (isProcessingRuleNameError) {
      setErrorName(state.name);
    } else {
      setErrorName('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessingRuleNameError]);

  return {
    // Main form
    state,
    setState,
    fields,
    shouldConfirmOnClose,
    setShouldConfirmOnClose,

    // Tiers
    tiersValues,
    getFieldValuesById,
    handleChangeValues,
    handleRemoveById,
    handleAddNewTier,

    // Modal controls
    onCloseModalHandler,
    handleSubmit,
    isSubmitted,
    isLoading,
    isServerError,
    setServerError,
  };
};

export default useProcessingRuleModal;
